
import { BlogPost } from '@/data/blogs';
import { ServiceData } from '@/data/services';
import { authors, authorSameAs } from '@/data/authors';

const SITE_URL = 'https://ortemtech.com';

// Strip HTML tags for use in JSON-LD fields (must be plain text)
const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
const ORGANIZATION_NAME = 'Ortem Technologies';
const LOGO_URL = `${SITE_URL}/assets/ot-logo-mark.png`;

// Global Organization Schema
export const generateOrganizationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: ORGANIZATION_NAME,
    legalName: 'Ortem Technologies LLC',
    alternateName: ['OrtemTech', 'Ortem Technologies LLC', 'Ortem-Technologies'],
    url: SITE_URL,
    logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
        width: 512,
        height: 512
    },
    image: LOGO_URL,
    foundingDate: '2012',
    numberOfEmployees: {
        '@type': 'QuantitativeValue',
        value: 200
    },
    description: 'Custom software, AI, web and mobile app development company founded in 2012. US-based project management with world-class engineering delivery. 1,000+ projects delivered for 300+ clients across USA, UK, Australia, UAE and APAC.',
    address: {
        '@type': 'PostalAddress',
        streetAddress: '16192 Coastal Highway',
        addressLocality: 'Lewes',
        addressRegion: 'DE',
        postalCode: '19958',
        addressCountry: 'US'
    },
    contactPoint: [
        {
            '@type': 'ContactPoint',
            telephone: '+1-519-502-1577',
            contactType: 'sales',
            email: 'sales@ortemtech.com',
            areaServed: 'Worldwide',
            availableLanguage: 'English'
        },
        {
            '@type': 'ContactPoint',
            telephone: '+1-519-502-1577',
            contactType: 'customer support',
            availableLanguage: 'English'
        }
    ],
    sameAs: [
        'https://www.linkedin.com/company/ortem-technologies-llc/',
        'https://www.youtube.com/@ortemtechnologies',
        'https://clutch.co/profile/ortem-technologies/',
        'https://www.goodfirms.co/company/ortem-technologies-llc',
        'https://techbehemoths.com/company/ortem-technologies-llc',
        'https://www.g2.com/products/ortem-technologies-ortem-technologies/reviews',
        'https://www.truefirms.co/company/Ortem-Technologies-LLC/3773',
        'https://x.com/ortemtech',
        'https://www.instagram.com/ortemtech/',
        'https://tracxn.com/d/companies/ortem-technologies/',
        'https://www.crunchbase.com/organization/ortem-technologies',
        'https://www.upwork.com/agencies/ortemtech'
    ],
    areaServed: { '@type': 'Place', name: 'Worldwide' },
    award: [
        'GoodFirms Industry Leader — Mobile App Development, United States (2026)',
        'GoodFirms Industry Leader — Web Development, Delaware (2026)',
        'GoodFirms Market Influencer — Software Development, Delaware (2026)',
        'GoodFirms Upcoming Achiever — Artificial Intelligence (NLP), United States (2026)',
    ],
    knowsAbout: [
        'Custom Software Development',
        'Mobile App Development',
        'AI Development',
        'Web Development',
        'Cloud Computing',
        'DevOps',
        'IoT Development',
        'eCommerce Development',
        'SaaS Development',
        'Staff Augmentation',
        'Enterprise Software Development',
        'AI Chatbot Development',
        'LLM Integration',
        'CRM Software Development',
        'ERP Development',
        'Fleet Management Software'
    ]
});

// AggregateRating for the Organization entity. Only valid on pages actually about
// Ortem as an org (home, about, testimonials) — Google does not support self-serving
// review rich results elsewhere, and duplicating it across every page's Organization
// block (same #organization @id) triggers GSC "invalid items" errors. Attach explicitly
// on those pages only; never spread into the base Organization schema used site-wide.
export const ORGANIZATION_AGGREGATE_RATING = {
    '@type': 'AggregateRating',
    ratingValue: 5.0,
    reviewCount: 47,
    bestRating: 5,
    worstRating: 1,
    ratingExplanation: 'Average rating across Clutch, GoodFirms, and G2 verified reviews'
};

// Global WebSite Schema (for Sitelinks Searchbox eligibility)
export const generateWebSiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: ORGANIZATION_NAME,
    url: SITE_URL,
    description: 'Custom software, AI, web and mobile app development company. 1,000+ projects delivered for 300+ clients worldwide.',
    publisher: {
        '@id': `${SITE_URL}/#organization`
    },
    potentialAction: {
        '@type': 'SearchAction',
        target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/blog/?search={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
    }
});

// ProfessionalService / LocalBusiness Schema
export const generateProfessionalServiceSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE_URL}/#professional-service`,
    name: ORGANIZATION_NAME,
    url: SITE_URL,
    image: {
        '@type': 'ImageObject',
        url: LOGO_URL
    },
    telephone: '+1-519-502-1577',
    email: 'sales@ortemtech.com',
    priceRange: '$$$',
    currenciesAccepted: 'USD',
    paymentAccepted: 'Credit Card, Invoice, Bank Transfer',
    openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00'
    },
    address: {
        '@type': 'PostalAddress',
        streetAddress: '16192 Coastal Highway',
        addressLocality: 'Lewes',
        addressRegion: 'DE',
        postalCode: '19958',
        addressCountry: 'US'
    },
    geo: {
        '@type': 'GeoCoordinates',
        latitude: 38.7746,
        longitude: -75.1393
    },
    areaServed: { '@type': 'Place', name: 'Worldwide' },
    hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Software Development Services',
        itemListElement: [
            'Custom Software Development',
            'Web Development',
            'Mobile App Development',
            'AI & Machine Learning Development',
            'Cloud & DevOps Services',
            'Cloud Computing Solutions',
            'eCommerce Development',
            'IoT Development',
            'Cybersecurity Services',
            'QA & Software Testing',
            'Digital Marketing & SEO',
            'Enterprise Mobility Solutions'
        ].map((name, i) => ({
            '@type': 'Offer',
            position: i + 1,
            itemOffered: {
                '@type': 'Service',
                name,
                provider: { '@id': `${SITE_URL}/#organization` }
            }
        }))
    }
});

// Breadcrumb Schema
export const generateBreadcrumbSchema = (items: { name: string; item: string }[]) => {
    try {
        const id = `${SITE_URL}${items[items.length - 1]?.item}#breadcrumb`;
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            '@id': id,
            itemListElement: items.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.name,
                item: `${SITE_URL}${item.item}`
            }))
        };
    } catch (e) {
        console.error('Error generating breadcrumb schema', e);
        return {};
    }
};

// Helper to format date as YYYY-MM-DD (timezone-safe).
// Parses dates as UTC so output never shifts a day based on the build
// machine's local timezone. Handles: "May 7, 2026", "May 2026", "2026-06-06".
const MONTHS: Record<string, number> = {
    january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
    july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
};

const formatDate = (dateString: string): string => {
    const todayUTC = () => new Date().toISOString().split('T')[0];
    if (!dateString || typeof dateString !== 'string') return todayUTC();
    const str = dateString.trim();

    // ISO: YYYY-MM-DD (optionally with time) — already correct, normalize to date part
    const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

    // "Month D, YYYY" or "Month D YYYY"
    const md = str.match(/^([A-Za-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})$/);
    if (md) {
        const m = MONTHS[md[1].toLowerCase()];
        if (m) return `${md[3]}-${String(m).padStart(2, '0')}-${String(Number(md[2])).padStart(2, '0')}`;
    }

    // "Month YYYY" (no day) — default to the 1st
    const my = str.match(/^([A-Za-z]+)\.?\s+(\d{4})$/);
    if (my) {
        const m = MONTHS[my[1].toLowerCase()];
        if (m) return `${my[2]}-${String(m).padStart(2, '0')}-01`;
    }

    // Fallback: parse and read UTC components (avoids local-midnight day shift)
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    }
    return todayUTC();
};

// Article/Blog Post Schema
export const generateArticleSchema = (post: BlogPost) => {
    const postUrl = `${SITE_URL}/blog/${post.slug}/`;
    const isOrgAuthor = !post.author || post.author === 'Ortem Team' || post.author?.toLowerCase().includes('ortem');
    // Resolve the real author-page slug from authors.ts so the Person URL never
    // 404s (name-derived slugs drift: "Ortem AI Research Team" ≠ ortem-ai-team).
    const authorEntry = post.author ? authors.find(a => a.name === post.author) : undefined;
    const authorSlug = authorEntry?.slug ?? (post.author ? post.author.toLowerCase().replace(/\s+/g, '-') : '');
    const imageUrl = post.image
        ? (post.image.startsWith('http') ? post.image : `${SITE_URL}${post.image}`)
        : LOGO_URL;

    return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        '@id': `${postUrl}#article`,
        headline: post.title.length > 110 ? post.title.slice(0, 110) : post.title,
        name: post.title,
        description: post.metaDescription || post.excerpt,
        url: postUrl,
        image: {
            '@type': 'ImageObject',
            url: imageUrl,
            width: 1200,
            height: 630
        },
        datePublished: formatDate(post.date),
        dateModified: formatDate(post.updatedDate || post.date),
        author: isOrgAuthor ? {
            '@type': 'Organization',
            name: authorEntry?.name ?? ORGANIZATION_NAME,
            // Link the team byline to its author page (matches AuthorPage entity)
            // instead of collapsing every team author into the bare Organization.
            url: authorEntry ? `${SITE_URL}/blog/author/${authorEntry.slug}/` : SITE_URL,
            parentOrganization: {
                '@type': 'Organization',
                name: ORGANIZATION_NAME,
                '@id': `${SITE_URL}/#organization`
            }
        } : {
            '@type': 'Person',
            name: post.author,
            url: `${SITE_URL}/blog/author/${authorSlug}/`,
            ...(authorEntry && authorSameAs(authorEntry).length ? { sameAs: authorSameAs(authorEntry) } : {}),
            worksFor: {
                '@type': 'Organization',
                name: ORGANIZATION_NAME,
                url: SITE_URL
            }
        },
        // Reviewer of record, when one is named. Typed as Organization rather
        // than Person because the reviewers we credit are teams — typing a group
        // as Person is structurally false and validators flag a Person carrying
        // no individual identity.
        reviewedBy: post.reviewedBy
            ? {
                '@type': 'Organization',
                name: post.reviewedBy.name,
                description: post.reviewedBy.role,
                parentOrganization: {
                    '@type': 'Organization',
                    name: ORGANIZATION_NAME,
                    '@id': `${SITE_URL}/#organization`
                }
            }
            : undefined,
        publisher: {
            '@type': 'Organization',
            name: ORGANIZATION_NAME,
            '@id': `${SITE_URL}/#organization`,
            logo: {
                '@type': 'ImageObject',
                url: LOGO_URL,
                width: 512,
                height: 512
            }
        },
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': postUrl
        },
        isPartOf: {
            '@type': 'Blog',
            '@id': `${SITE_URL}/blog/#blog`,
            name: `${ORGANIZATION_NAME} Blog`,
            publisher: { '@id': `${SITE_URL}/#organization` }
        },
        articleSection: post.category,
        keywords: post.tags ? post.tags.join(', ') : undefined,
        wordCount: post.content ? Math.round(post.content.split(/\s+/).length) : undefined,
        about: {
            '@type': 'Thing',
            name: post.category
        },
        // Emit the post's source list as schema.org citation. The sources were
        // already rendered visibly at the foot of the article but were invisible
        // to parsers, so a post whose figures came from primary vendor pricing
        // looked identical to an unsourced opinion piece. Answer engines weigh
        // whether a claim is attributable, so this is the machine-readable half
        // of the references block that was already there.
        citation: post.sources && post.sources.length > 0
            ? post.sources.map(source => ({
                '@type': 'CreativeWork',
                name: source.title,
                url: source.url,
                ...(source.publisher ? { publisher: { '@type': 'Organization', name: source.publisher } } : {})
            }))
            : undefined,
        inLanguage: 'en-US'
    };
};

// Service Schema
export const generateServiceSchema = (service: ServiceData) => {
    try {
        if (!service) return {};
        const serviceUrl = `${SITE_URL}/services/${service.slug}/`;

        return {
            '@context': 'https://schema.org',
            '@type': 'Service',
            '@id': `${serviceUrl}#service`,
            name: service.title,
            url: serviceUrl,
            description: stripHtml(service.heroDescription || ''),
            serviceType: service.title,
            category: 'Software Development',
            provider: {
                '@type': 'Organization',
                name: ORGANIZATION_NAME,
                url: SITE_URL,
                '@id': `${SITE_URL}/#organization`,
                image: {
                    '@type': 'ImageObject',
                    url: LOGO_URL
                }
            },
            areaServed: { '@type': 'Place', name: 'Worldwide' },
            hasOfferCatalog: {
                '@type': 'OfferCatalog',
                name: service.title,
                itemListElement: service.services?.map((item) => ({
                    '@type': 'Offer',
                    description: stripHtml(item?.description || ''),
                    itemOffered: {
                        '@type': 'Service',
                        name: item?.title || '',
                        description: stripHtml(item?.description || ''),
                        provider: { '@id': `${SITE_URL}/#organization` }
                    }
                })) || []
            },
            ...(service.keywords && service.keywords.length > 0 ? { keywords: service.keywords.join(', ') } : {}),
            // No aggregateRating here. Google's review snippet spec does not list
            // Service as a supported host type, so every service page was reporting
            // "Invalid object type for field <parent_node>" in GSC and losing rich
            // result eligibility for the whole page. See ORGANIZATION_AGGREGATE_RATING
            // above for the org-level rating and where it may legitimately appear.
            mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': serviceUrl
            }
        };
    } catch (error) {
        console.error('Error generating service schema:', error);
        return {};
    }
};

// Generic Service Schema for standalone SEO landing pages (MVP, Flutter, etc.)
export const generateStandaloneServiceSchema = (data: {
    name: string;
    url: string;
    description: string;
    serviceType?: string;
}) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${data.url}#service`,
    name: data.name,
    url: data.url,
    description: data.description,
    serviceType: data.serviceType || data.name,
    category: 'Software Development',
    provider: {
        '@type': 'Organization',
        name: ORGANIZATION_NAME,
        url: SITE_URL,
        '@id': `${SITE_URL}/#organization`,
        image: { '@type': 'ImageObject', url: LOGO_URL }
    },
    areaServed: { '@type': 'Place', name: 'Worldwide' },
    mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': data.url
    }
});

// CollectionPage Schema for Blog and Case Studies listing pages
export const generateCollectionPageSchema = (data: {
    name: string;
    description: string;
    url: string;
}) => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${data.url}#collection`,
    name: data.name,
    description: data.description,
    url: data.url,
    isPartOf: {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`
    },
    publisher: {
        '@type': 'Organization',
        name: ORGANIZATION_NAME,
        '@id': `${SITE_URL}/#organization`
    },
    inLanguage: 'en-US'
});

// FAQ Schema
export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => {
    try {
        if (!faqs || faqs.length === 0) return {};

        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: stripHtml(faq.answer)
                }
            }))
        };
    } catch (e) {
        console.error('Error generating FAQ schema', e);
        return {};
    }
};

// SpeakableSpecification Schema — for AEO, voice search, and AI Overviews
// cssSelector should point to elements containing the most informative content
export const generateSpeakableSchema = (pageUrl: string, cssSelectors: string[] = []) => {
    const selectors = cssSelectors.length > 0
        ? cssSelectors
        : ['h1', '.hero-description', '.page-summary', '[data-speakable]'];

    return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: selectors
        }
    };
};

// VideoObject Schema
export const generateVideoSchema = (data: {
    name: string;
    description: string;
    thumbnailUrl: string;
    uploadDate: string;
    contentUrl?: string;
    embedUrl?: string;
    duration?: string;
}) => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: data.name,
    description: data.description,
    thumbnailUrl: data.thumbnailUrl,
    uploadDate: data.uploadDate,
    ...(data.contentUrl && { contentUrl: data.contentUrl }),
    ...(data.embedUrl && { embedUrl: data.embedUrl }),
    ...(data.duration && { duration: data.duration }),
    publisher: {
        '@type': 'Organization',
        name: ORGANIZATION_NAME,
        '@id': `${SITE_URL}/#organization`,
        logo: { '@type': 'ImageObject', url: LOGO_URL }
    }
});

// SoftwareApplication Schema — for products/tools like App Cost Calculator, ROI Calculator
export const generateSoftwareAppSchema = (data: {
    name: string;
    description: string;
    url: string;
    applicationCategory?: string;
    operatingSystem?: string;
    offers?: { price: string; priceCurrency: string };
}) => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: data.name,
    description: data.description,
    url: data.url,
    applicationCategory: data.applicationCategory || 'BusinessApplication',
    operatingSystem: data.operatingSystem || 'Web',
    offers: data.offers ? {
        '@type': 'Offer',
        price: data.offers.price,
        priceCurrency: data.offers.priceCurrency
    } : {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
    },
    author: {
        '@type': 'Organization',
        name: ORGANIZATION_NAME,
        '@id': `${SITE_URL}/#organization`
    }
});
